import React, { useContext, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { isTickmillCompanyDisabled } from '@/utils/isTickmillCompanyDisabled'
import {
  ReferralMaterialsCategoriesDto,
  UpdateReferralMaterialCategoryDto,
} from 'api/ClientArea/ReferralMaterialsDto'
import { Language } from 'api/Clients/Types/Language'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { AuthSessionContext } from 'utils/AuthContext'
import { convertDateToUTC, createDateAsUTC } from 'utils/date'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'

import { ReferralMaterialsCategoryUrlsField } from './ReferralMaterialsCategoryUrlsField'

export interface ReferralMaterialsCategoryDetailsFormValues {
  name: string
  alias: string
  tickmillCompanyId: number | undefined
  urls: { baseUrl?: number; targetUrl: string }[]
  enabled: boolean
  endDateTime: string | null
}

const ReferralMaterialsCategoryFormUI: React.FC<
  FormikProps<ReferralMaterialsCategoryDetailsFormValues> & OuterProps
> = (props) => {
  const { resetForm, submitForm, isValid, isSubmitting, values, errors, setFieldValue } = props
  const { baseUrlTypes, tickmillCompanies } = props
  const [auth] = useContext(AuthSessionContext)

  const isEndDateExpired = useMemo(() => {
    if (!values.endDateTime) {
      return false
    }
    return createDateAsUTC(values.endDateTime, false, true) < new Date()
  }, [values.endDateTime])
  const dateFormatType = auth?.user?.dateFormatType?.name
  const minEndDate = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }, [])
  const companyOptions = useMemo(
    () =>
      tickmillCompanies.map((company) => ({
        id: company.id,
        name: company.name,
        disabled: isTickmillCompanyDisabled(company),
      })),
    [tickmillCompanies]
  )

  const [isEdit, setEdit] = useState<boolean>(false)

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSave = () => {
    submitForm()
  }

  const renderMenu = () => (
    <DropdownMenuItem
      onSelect={() => props.setAuditLogModal(true)}
      data-test='audit-log-menu-button'
    >
      Audit Log
    </DropdownMenuItem>
  )

  return (
    <Form>
      <DataGrid
        rightAddon={
          <ButtonGroup>
            {isEdit ? (
              <React.Fragment>
                <Button
                  type='button'
                  variant='outline'
                  className='is-danger'
                  onClick={handleCancel}
                  data-test='cancel-button'
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  disabled={!isValid || isSubmitting}
                  onClick={handleSave}
                  data-test='save-button'
                >
                  Save
                </Button>
              </React.Fragment>
            ) : (
              <Button type='button' onClick={() => setEdit(true)} data-test='edit-button'>
                Edit
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    aria-label='More options'
                    className='more-button'
                  />
                }
              >
                <ChevronDown />
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard title={!isEdit ? 'Category Settings' : 'Edit Category Settings'}>
            <FormField error={errors.name} name='name' label='Category Name' readOnly={!isEdit} />
            <FormField error={errors.alias} name='alias' label='Alias' readOnly={!isEdit} />
            <FormSelectField
              error={errors.tickmillCompanyId}
              name='tickmillCompanyId'
              label='Tickmill Entity'
              value={values.tickmillCompanyId}
              options={companyOptions}
              readOnly={!isEdit}
              showSearch
              allowClear
              onChange={(value) =>
                setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
              }
            />
            <ReferralMaterialsCategoryUrlsField
              values={values}
              baseUrlTypes={baseUrlTypes}
              isEdit={isEdit}
              setFieldValue={setFieldValue}
            />
            <FormDateField
              error={errors.endDateTime}
              name='endDateTime'
              label='End Date'
              readOnly={!isEdit}
              dateFormatType={dateFormatType}
              minDate={minEndDate}
            />
            <FormCheckboxField
              error={errors.enabled}
              name='enabled'
              label='Active'
              value={values.enabled}
              disabled={!isEdit || isEndDateExpired}
            />
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

const dataToSubmit = (
  values: Required<ReferralMaterialsCategoryDetailsFormValues>
): UpdateReferralMaterialCategoryDto => {
  return {
    name: values.name || '',
    endDateTime: values.endDateTime
      ? createDateAsUTC(values.endDateTime, false, true).toISOString()
      : null,
    urls: values.urls
      .filter((url) => url.baseUrl)
      .map((u) => ({
        baseUrlTypeId: u.baseUrl,
        targetUrl: u.targetUrl,
      })),
    enabled: values.enabled || false,
    tickmillCompanyId: values?.tickmillCompanyId || 0,
    alias: values.alias || '',
  }
}

interface OuterProps {
  data: ReferralMaterialsCategoriesDto
  languages: Language[]
  tickmillCompanies: TickmillCompanyDto[]
  baseUrlTypes: NameDto[]

  setAuditLogModal(value: boolean): void
  onSubmit(values: FormSubmitValues<UpdateReferralMaterialCategoryDto>): Promise<void>
}

const getDefaultCompanyId = (companies: TickmillCompanyDto[]): TickmillCompanyTypes | undefined => {
  return companies.find((x) => x.id === TickmillCompanyTypes.Tickmill_SC_Company)?.id
}

export const ReferralMaterialsCategoryDetailsForm = withFormik<
  OuterProps,
  ReferralMaterialsCategoryDetailsFormValues
>({
  mapPropsToValues: (props) => {
    const { data } = props
    const defaultCompany = getDefaultCompanyId(props.tickmillCompanies)

    return {
      name: data?.name || '',
      urls: data?.urls?.length
        ? data.urls.map((u) => ({
            baseUrl: u.baseUrlType.id,
            targetUrl: u.targetUrl,
          }))
        : [{ targetUrl: '' }],
      enabled: data?.isActive ?? false,
      endDateTime: data.endDateTime ? convertDateToUTC(data.endDateTime).toISOString() : null,
      tickmillCompanyId: data?.tickmillCompany?.id ?? defaultCompany,
      alias: data?.alias || '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<ReferralMaterialsCategoryDetailsFormValues> = {}
    const urlsErrors: { targetUrl?: string }[] = []

    if (!values.name) {
      errors.name = 'Required'
    }

    if (!values.alias) {
      errors.alias = 'Required'
    }

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }

    values.urls.forEach((u, index) => {
      const urlError: { baseUrl?: number; targetUrl?: string } = {}

      if (u.baseUrl === 3 && !u.targetUrl) {
        urlError.targetUrl = 'Required'
      }

      if (Object.keys(urlError).length > 0) {
        urlsErrors[index] = urlError
      }
    })

    if (urlsErrors.some(Boolean)) {
      errors.urls = urlsErrors
    }

    if (isEmpty(values.enabled)) {
      errors.enabled = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
  validateOnChange: true,
})(ReferralMaterialsCategoryFormUI)
